import { FC } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Star, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import confetti from 'canvas-confetti';

interface LessonSummaryProps {
  points: {
    video: number;
    audio: number;
    reading: number;
    quiz: number;
    total: number;
  };
  activitiesCompleted: {
    video: boolean;
    audio: boolean;
    reading: boolean;
    quiz: boolean;
  };
  cerebrixEarned: number;
  onClose: () => void;
}

export const LessonSummary: FC<LessonSummaryProps> = ({
  points,
  activitiesCompleted,
  cerebrixEarned,
  onClose
}) => {
  // Lanzar confetti cuando se muestra el componente
  const showConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full">
        <div className="text-center space-y-4">
          <h2 className="text-2xl font-bold">¡Lección Completada!</h2>
          
          {/* Puntos Totales */}
          <div className="text-4xl font-bold text-primary">
            {points.total} puntos
          </div>

          {/* Cerebrix Ganados */}
          <div className="text-lg text-blue-500 flex items-center justify-center gap-2">
            <Zap className="w-6 h-6" />
            +{cerebrixEarned} Cerebrix
          </div>

          {/* Desglose de Puntos */}
          <div className="space-y-3 text-left mt-6">
            <h3 className="font-semibold text-gray-700 mb-2">Desglose de Puntos:</h3>
            
            {/* Video */}
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Video</span>
              <div className="flex items-center gap-2">
                <Star className={`w-4 h-4 ${activitiesCompleted.video ? 'text-yellow-500' : 'text-gray-300'}`} />
                <span className={`font-medium ${activitiesCompleted.video ? 'text-primary' : 'text-gray-400'}`}>
                  {points.video} pts
                </span>
              </div>
            </div>

            {/* Audio */}
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Audio</span>
              <div className="flex items-center gap-2">
                <Star className={`w-4 h-4 ${activitiesCompleted.audio ? 'text-yellow-500' : 'text-gray-300'}`} />
                <span className={`font-medium ${activitiesCompleted.audio ? 'text-primary' : 'text-gray-400'}`}>
                  {points.audio} pts
                </span>
              </div>
            </div>

            {/* Lectura */}
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Lectura</span>
              <div className="flex items-center gap-2">
                <Star className={`w-4 h-4 ${activitiesCompleted.reading ? 'text-yellow-500' : 'text-gray-300'}`} />
                <span className={`font-medium ${activitiesCompleted.reading ? 'text-primary' : 'text-gray-400'}`}>
                  {points.reading} pts
                </span>
              </div>
            </div>

            {/* Quiz */}
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Quiz</span>
              <div className="flex items-center gap-2">
                <Star className={`w-4 h-4 ${activitiesCompleted.quiz ? 'text-yellow-500' : 'text-gray-300'}`} />
                <span className={`font-medium ${activitiesCompleted.quiz ? 'text-primary' : 'text-gray-400'}`}>
                  {points.quiz} pts
                </span>
              </div>
            </div>

            {/* Total */}
            <div className="flex items-center justify-between pt-2 border-t mt-2">
              <span className="font-semibold">Total</span>
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-500" />
                <span className="font-bold text-primary">{points.total} pts</span>
              </div>
            </div>
          </div>

          <Button
            onClick={() => {
              showConfetti();
              onClose();
            }}
            className="w-full mt-6"
          >
            ¡Continuar!
          </Button>
        </div>
      </div>
    </motion.div>
  );
};
